import {
  customerCanAccessInvoicesAndReports,
  customerHasEL,
  customerHasElectricity,
  customerIsCompany,
  customerIsRecentElhandelOnly,
  hasProduction,
  userHasFJVEffekttariff,
  userHasFlowCharge,
  userHasPeakTariffSingle,
  userHasPeakTariffThree,
  userIsCustomer,
  userIsInactive,
  userIsIndividual,
  userIsNonCustomer,
  userIsUpcoming,
} from '@/helpers/customerUtils';
import { propertyHasFjvServiceAgreement } from '@/helpers/facilityUtils';
import { isProductionEnvironment } from '@/helpers/utils';
import { hideFjvPriceCalculator } from '@/pages/fjv-price-calculator/priceHelper';
import { InfraServiceName } from '@/pages/infra-service/types';
import { InfraService } from '@/types/InfraService';
import Link from 'ui/components/Link';
import {
  IconAccountTree,
  IconAcUnit,
  IconBarChart,
  IconBathtub,
  IconBolt,
  IconDashboard,
  IconDescription,
  IconHistoryEdu,
  IconInvertColors,
  IconListAlt,
  IconWhatshot,
} from 'ui/Icons';
import { Box } from 'ui/mui';
import { GetRoutesProps, RouteType } from './types';

export const notFoundRoute: RouteType = {
  path: '*',
  title: '404 sida',
  pageHeaderOptions: {
    hide: true,
    title: '404 sida',
  },
};

export const getRoutes = ({
  selectedCustomer,
  selectedProperty,
  hasManualDrainReading,
  organizationStructure,
  hasPowersOfAttorney,
  hasHeatExchangeDevices,
  groupedContractsWithExpired,
}: GetRoutesProps) => {
  const toggled = selectedCustomer?.useNewLandingPage ?? false;

  const showInvoicesAndReports = customerCanAccessInvoicesAndReports(
    selectedCustomer,
    groupedContractsWithExpired,
  );

  const isRecentElhandelOnlyCustomer = customerIsRecentElhandelOnly(
    selectedCustomer,
    groupedContractsWithExpired,
  );

  const peakPowerRouteTitle = 'Effekttoppar';
  const peakPowerRouteSubtitle =
    'Här kan du följa dina effekttoppar och se när elanvändningen varit som högst under månaden';

  const userRoutes: RouteType[] = [
    {
      path: '/non-customer',
      component: 'NonCustomer',
      title: 'Översikt',
      icon: <IconDashboard />,
      color: 'primary',
      section:
        !userIsCustomer(selectedCustomer) && !userIsUpcoming(selectedCustomer)
          ? 'overview'
          : null,
      pageHeaderOptions: {
        hide: !userIsCustomer(selectedCustomer),
      },
    },
    {
      path: '/',
      component: 'Landing',
      exact: true,
      icon: <IconDashboard />,
      title: 'Översikt',
      color: 'primary',
      section:
        selectedCustomer &&
        (userIsCustomer(selectedCustomer) || userIsUpcoming(selectedCustomer))
          ? 'overview'
          : null,
      pageHeaderOptions: {
        title: 'Översikt',
        hide: toggled,
      },
    },
    {
      path: '/fjarrvarme',
      redirectTo: '/fjarrvarme/fjarrvarmeforbrukning',
      component: 'InfraService',
      icon: <IconWhatshot />,
      title: 'Fjärrvärme',
      color: 'yellow',
      section: 'services',
      utilityType: ['FJV'],
      infraService: 'FJV',
      childRoutes: [
        {
          path: '/fjarrvarme/fjarrvarmeforbrukning',
          component: 'InfraService',
          icon: <IconWhatshot />,
          title: 'Fjärrvärme\u00ADförbrukning',
          utilityType: ['FJV'],
          infraService: 'FJV',
        },
        {
          path: '/fjarrvarme/avkylning',
          component: 'Cooling',
          title: `Avkylning`,
          pageHeaderOptions: {
            backHref: '/fjarrvarme',
            backText: 'Fjärrvärme',
            subtitle:
              'Avkylningen visar hur effektiv din anläggning är. Har anläggningen en hög avkylning får den ut mer energi (MWh) från vattenflödet (m³) som pumpas till den. Avkylningen är skillnaden i temperatur mellan inkommande fjärrvärmevatten och utgående returvatten.',
          },
        },
        {
          path: '/fjarrvarme/bas-och-spetsenergi',
          component: 'FJVEnergy',
          title: `Bas- och spetsenergi`,
          hidden: !userHasFJVEffekttariff(selectedCustomer),
          pageHeaderOptions: {
            backHref: '/fjarrvarme',
            backText: 'Fjärrvärme',
            subtitle: 'Analysera din bas- samt spetsenergi.',
          },
        },
        {
          path: '/fjarrvarme/flodespremie',
          component: 'FlowCharge',
          title: `Flödespremie`,
          hidden: !userHasFlowCharge(selectedCustomer),
          pageHeaderOptions: {
            subtitle: (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span>
                  Nyttjar din anläggning värmen i vattenflödet på ett effektivt
                  sätt kan du få en flödespremie under månaderna oktober till
                  april.
                </span>
                <Link
                  href='https://www.malarenergi.se/foretag/varme-kyla-foretag/fjarrvarme-foretag/priser-fjarrvarme/flodespremie/'
                  external
                >
                  Mer om flödespremie på malarenergi.se
                </Link>
              </Box>
            ),
          },
        },
        {
          path: '/fjarrvarme/jamforelse',
          component: 'Comparison',
          title: `Jämför förbrukning`,
          pageHeaderOptions: {
            backHref: '/fjarrvarme',
            backText: 'Fjärrvärme',
            title: 'Jämför förbrukning med andra',
            subtitle:
              'Alla fjärrvärmeanläggningar delas in i en kategori. När en månad är slut beräknas kategorins genomsnittsförbrukning utifrån månadsförbrukningen för kategorins alla anläggningar. Här visas hur mycket högre eller lägre förbrukning (i procent, %) dina anläggningar hade förra månaden jämfört med kategorins genomsnittsförbrukning. Jämförelsen tar inte hänsyn till uppvärmd volym vilket kan ge en orättvis bild om du har större volym än snittet.',
          },
        },
        {
          path: '/fjarrvarme/avtal',
          component: 'DistanceHeatingContract',
          title: `Teckna ${
            userIsIndividual(selectedCustomer) ? 'serviceavtal' : 'driftavtal'
          }`,
          hidden: userIsIndividual(selectedCustomer)
            ? propertyHasFjvServiceAgreement(selectedProperty)
            : false,
          pageHeaderOptions: {
            subtitle: userIsIndividual(selectedCustomer)
              ? 'Skicka in en ansökan så kontaktar vid dig inom kort angående ditt serviceavtal.'
              : 'Fjärrvärme är marknadens mest pålitliga värmekälla med en otroligt hög leveranssäkerhet men fjärrvärmecentralen består trots allt av en mängd tekniska komponenter som behöver ses över och underhållas. Detta kan delas mellan oss eller så tar vi hand om allt inklusive risker, beroende på hur enkelt just du vill ha det.',
          },
        },
        {
          path: '/fjarrvarme/berakna-din-fjarrvarmekostnad',
          component: 'FjvPriceCalculator',
          title: 'Beräknad fjärrvärmekostnad',
          hidden: hideFjvPriceCalculator(selectedCustomer),
          pageHeaderOptions: {
            subtitle:
              'Välj månad och se hur din fjärrvärmekostnad förändras per månad.',
          },
        },
        {
          path: '/fjarrvarme/flode',
          component: 'Flow',
          hidden: !userIsIndividual(selectedCustomer),
          title: 'Flöde',
          pageHeaderOptions: {
            subtitle:
              'Flödet visar hur mycket fjärrvärmevatten som passerar din anläggning. Ju bättre din anläggning kyler ner vattnet, desto lägre blir flödet och därmed din kostnad.',
          },
        },
        {
          path: '/fjarrvarme/stall-inomhustemperatur',
          component: 'HeatExchangeControl',
          title: 'Ställ inomhus\u00ADtemperatur',
          hidden: !hasHeatExchangeDevices,
          pageHeaderOptions: {
            subtitle: 'Ställ in önskad inomhustemperatur för bästa komfort.',
          },
        },
      ],
    },
    {
      path: '/undermatning-fjarrvarme',
      component: 'InfraService',
      icon: <IconWhatshot />,
      title: 'Undermätning fjärrvärme',
      color: 'yellow',
      section: 'services',
      utilityType: ['Umfjv'],
      infraService: 'UMFJV',
      childRoutes: [
        {
          path: '/undermatning-fjarrvarme/stall-inomhustemperatur',
          component: 'HeatExchangeControl',
          title: 'Ställ inomhus\u00ADtemperatur',
          hidden: !hasHeatExchangeDevices,
          pageHeaderOptions: {
            subtitle: 'Ställ in önskad inomhustemperatur för bästa komfort.',
          },
        },
      ],
    },
    {
      path: '/el',
      redirectTo: '/el/elforbrukning',
      component: 'InfraService',
      icon: <IconBolt />,
      title: 'El',
      color: 'greenLight',
      section: 'services',
      utilityType: ['EL, ELPROD'],
      infraService: 'EL',
      childRoutes: [
        {
          path: '/el/elforbrukning',
          component: 'InfraService',
          icon: <IconBolt />,
          title: 'Elförbrukning',
          utilityType: ['EL, ELPROD'],
          infraService: 'EL',
        },
        {
          path: '/el/eloverskott',
          component: 'InfraService',
          utilityType: ['ELPROD'],
          infraService: 'ELPROD',
          title: 'Elöverskott',
          hidden: !hasProduction(
            selectedProperty?.infraServices?.find(
              (infraService: InfraService) => infraService?.name === 'EL',
            ),
          ),
        },
        {
          path: '/el/spotpriser',
          icon: <IconBarChart />,
          component: 'Spotpriser',
          section: 'tools',
          title: 'Spotpriset',
          hidden:
            (!userIsCustomer(selectedCustomer) &&
              !userIsUpcoming(selectedCustomer)) ||
            !customerHasElectricity(selectedCustomer),
          pageHeaderOptions: {
            subtitle:
              'Spotpriset utgör grunden i det rörliga elpriset och visas i öre/kWh. Perfekt för dig som vill påverka din elkostnad genom att använda el de kvartar det är billigare.',
          },
        },
        {
          path: '/el/han-port',
          component: 'ManageHanPort',
          title: 'HAN-port',
          hidden: !selectedCustomer?.utilities?.some(
            (utility) => utility.name === ('EL' satisfies InfraServiceName),
          ),
        },
        {
          path: '/el/effekttopp-elnat',
          component: 'PeakPower',
          title: peakPowerRouteTitle,
          hidden: !userHasPeakTariffSingle(selectedCustomer),
          pageHeaderOptions: {
            subtitle: peakPowerRouteSubtitle,
          },
        },
        {
          path: '/el/berakna-din-elnatskostnad',
          component: 'ElectricityPriceCalculator',
          title: 'Beräkna din elnätskostnad',
          hidden: !userHasPeakTariffSingle(selectedCustomer),
          pageHeaderOptions: {
            subtitle:
              'Beräkna din förväntade elnätskostnad baserat på förbrukning och effekttopp',
          },
        },
        {
          path: '/el/fullmakter',
          component: 'PowersOfAttorney',
          title: 'Fullmakter',
          hidden: !hasPowersOfAttorney,
          pageHeaderOptions: {
            subtitle:
              'Få koll på dina fullmakter – godkänn eller avslå inkomna förfrågningar och följ status på både aktiva och avslutade fullmakter.',
          },
        },
        {
          path: '/el/access-logs',
          component: 'AccessLogs',
          title: 'Åtkomstlogg',
          hidden: !customerHasEL(selectedCustomer),
          pageHeaderOptions: {
            subtitle:
              'Här ser du en komplett logg över vilka som har hämtat eller visat uppgifter om din elförbrukning — både du själv och externa aktörer med fullmakt.',
          },
        },
      ],
    },
    {
      path: '/bestall-power-hub',
      component: 'PowerHub',
      title: 'Följ din elförbrukning i realtid',

      pageHeaderOptions: {
        subtitle:
          'Med en Power Hub från Mälarenergi ser du exakt hur mycket el du använder – just nu. Den kopplas enkelt till din elmätare och visar din förbrukning direkt i appen. Ett smart val för dig som vill minska din elförbrukning, förstå dina kostnader och fatta bättre beslut i vardagen.',
      },
    },
    {
      path: '/undermatning-el',
      component: 'InfraService',
      icon: <IconBolt />,
      title: 'Undermätning el',
      color: 'greenLight',
      section: 'services',
      utilityType: ['IMDEL'],
      infraService: 'IMDEL',
    },
    {
      path: '/vatten-avlopp',
      redirectTo: '/vatten-avlopp/vattenforbrukning',
      component: 'InfraService',
      icon: <IconInvertColors />,
      title: 'Vatten',
      color: 'turquoise',
      section: 'services',
      utilityType: ['Va'],
      infraService: 'VA',
      childRoutes: [
        {
          path: '/vatten-avlopp/vattenforbrukning',
          component: 'InfraService',
          icon: <IconInvertColors />,
          title: 'Vattenförbrukning',
          utilityType: ['Va'],
          infraService: 'VA',
        },
        {
          path: '/vatten-avlopp/avlasning',
          component: 'MeterReadings',
          title: 'Vattenavläsning',
          infraService: 'VA',
          pageHeaderOptions: {
            title: hasManualDrainReading
              ? 'Lämna mätarställning'
              : 'Vattenavläsning',
            subtitle: hasManualDrainReading
              ? 'Här kan du enkelt lämna mätarställning på din vattenmätare.'
              : undefined,
          },
        },
      ],
    },
    {
      path: '/undermatning-vatten',
      component: 'InfraService',
      icon: <IconInvertColors />,
      title: 'Undermätning Vatten',
      color: 'turquoise',
      section: 'services',
      utilityType: ['Umva'],
      infraService: 'UMVA',
    },
    {
      path: '/tappvarmvatten',
      component: 'InfraService',
      icon: <IconBathtub />,
      title: 'Tappvarmvatten',
      color: 'redLight',
      section: 'services',
      utilityType: ['Tvv'],
      infraService: 'TVV',
    },

    {
      path: '/fjarrkyla',
      component: 'InfraService',
      icon: <IconAcUnit />,
      title: 'Fjärrkyla',
      color: 'oceanGreen',
      section: 'services',
      utilityType: ['Fjk'],
      infraService: 'FJK',
    },
    {
      path: '/fakturor',
      component: 'Invoices',
      icon: <IconDescription />,
      title: 'Fakturor',
      color: 'orange',
      section: showInvoicesAndReports ? 'tools' : null,
      childRoutes: [
        {
          path: '/andra-fakturareferens/',
          component: 'InvoiceReference',
          title: 'Fakturareferenser',
          hidden: userIsIndividual(selectedCustomer),
        },
      ],
      pageHeaderOptions: {
        subtitle: isRecentElhandelOnlyCustomer ? (
          <>
            Här hittar du dina tidigare fakturor.
            <br />
            Du har tillgång till dem i upp till 12 månader efter att avtalet
            avslutats.
            <br />
            Därefter kan uppgifter beställas via kundcenter mot en administrativ
            avgift.
          </>
        ) : (
          'Här hittar du alla dina fakturor.'
        ),
      },
    },
    {
      path: '/teckna/utjamnad-betalning',
      component: 'UtjamnadBetalning',
      title: 'Utjämnad betalning',
      pageHeaderOptions: {
        backHref: '/fakturor',
        backText: 'Fakturor',
        subtitle:
          'Utjämnad betalning passar dig som vill fördela dina kostnader jämnt över hela året. Du får en jämnare spridning på dina betalningar till oss genom att vi delar upp din beräknade årskostnad i tolv lika stora delar. Om din användning eller dina kostnader ökar eller minskar, justeras månadsbeloppet. Justeringen sker minst en gång per år. Avgiften för tjänsten Utjämnad betalning är 19 kronor/månad.',
      },
    },
    {
      path: '/bekrafta_konto/:code',
      component: 'ConfirmAccount',
      title: 'Bekräfta inbjudan',
    },
    {
      path: '/el/tidigare-effekttoppar',
      component: 'PeakPowerTriple',
      title: 'Tidigare effekttoppar',
      hidden: !userHasPeakTariffThree(selectedCustomer),
    },
    {
      path: '/debug',
      component: 'Debug',
      hidden: isProductionEnvironment(),
    },
    {
      path: '/meddelanden',
      redirect: '/profile?tab="messages"',
    },
    {
      path: '/profile',
      component: 'Profile',
      title: selectedCustomer?.name || 'Profil',
      hidden: userIsNonCustomer(selectedCustomer),
      pageHeaderOptions: {
        maskTitle: true,
      },
    },
    {
      path: '/rapporter',
      component: 'Reports',
      icon: <IconListAlt />,
      title: 'Rapporter',
      section: showInvoicesAndReports ? 'tools' : null,
      color: 'orange',
      pageHeaderOptions: {
        subtitle:
          customerIsCompany(selectedCustomer) &&
          !isRecentElhandelOnlyCustomer ? (
            <span>
              Som företagskund kan du här ladda ner Excel-rapporter för
              företaget <strong>{selectedCustomer?.name}</strong>.
            </span>
          ) : isRecentElhandelOnlyCustomer ? (
            <>
              Här kan du ladda ner rapporter med dina tidigare uppgifter.
              <br />
              Du har tillgång till dem i upp till 12 månader efter att avtalet
              avslutats.
              <br />
              Därefter kan uppgifter beställas via kundcenter mot en
              administrativ avgift.
            </>
          ) : (
            'Här kan du som kund ladda ner Excel-rapporter.'
          ),
      },
      childRoutes: [],
    },
  ];

  const companyRoutes: RouteType[] = [
    {
      path: '/koncernrapport',
      component: 'ConcernReport',
      icon: <IconListAlt />,
      hidden: !organizationStructure,
      title: 'Koncernrapport',
      section: userIsInactive(selectedCustomer) ? 'tools' : null,
      pageHeaderOptions: {
        backHref: '/rapporter',
        backText: 'Rapporter',
        subtitle: (
          <span>
            Här kan du som företagskund beställa rapporter som omfattar alla
            bolag som ingår i koncernen{' '}
            <strong>{selectedCustomer?.name}</strong>
          </span>
        ),
      },
    },
    {
      path: '/avtal',
      component: 'Contracts',
      icon: <IconHistoryEdu />,
      title: 'Avtal',
      section:
        userIsCustomer(selectedCustomer) || userIsUpcoming(selectedCustomer)
          ? 'tools'
          : null,
      color: 'orange',
      pageHeaderOptions: {
        subtitle: 'Här hittar du alla dina avtal.',
      },
    },
    {
      path: '/koncernstruktur',
      component: 'ConcernStructure',
      hidden: !organizationStructure,
      icon: <IconAccountTree />,
      title: 'Koncernstruktur',
      section: 'tools',
      pageHeaderOptions: {
        subtitle: (
          <span>
            Här visas den koncernstruktur som{' '}
            <strong>{organizationStructure?.name}</strong> har hos Mälarenergi.
            Här visas alla bolag som är kopplade till moderbolaget.
          </span>
        ),
      },
    },
  ];

  const redirectedRoutes: RouteType[] = [
    {
      path: '/el/jamfora-elnatsavgift',
      redirect: '/el/effekttopp-elnat',
    },
    {
      path: '/exporter',
      redirect: '/rapporter',
    },
  ];

  return [...userRoutes, ...companyRoutes, ...redirectedRoutes];
};
